/**
 * 서버 API 호출 래퍼.
 * 인증은 httpOnly 쿠키로 자동 전달되므로 토큰을 JS 에서 다루지 않는다.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

/**
 * 로그인할 때 서버가 심어 둔(httpOnly 아닌) xadmin_csrf 쿠키 값을 읽는다.
 * 서버(middleware/csrf.ts)가 이 값과 X-CSRF-Token 헤더가 같은지 본다 -
 * SameSite=Lax 는 '다른 사이트'만 막지 같은 등록 도메인의 다른 서브도메인은
 * 못 막는데, 그 값을 이 쿠키에서만 읽을 수 있어(다른 오리진은 JS 로 못
 * 읽는다) 그 구멍을 메운다.
 */
function csrfToken(): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith('xadmin_csrf='))
    ?.split('=')[1];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = csrfToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { 'X-CSRF-Token': token } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    // 서버가 JSON 이 아닌 응답을 보내면(프록시 타임아웃, 화이트리스트 차단 등)
    // 원문을 그대로 오류 메시지로 잡는 대신 "Unexpected token ... is not valid
    // JSON" 이라는 알아보기 힘든 예외가 났다. 응답이 아예 안 된 것으로 본다.
    if (!res.ok) {
      throw new ApiError(res.status, 'NON_JSON_RESPONSE', text.slice(0, 200) || `요청에 실패했습니다 (HTTP ${res.status}).`);
    }
    throw new ApiError(res.status, 'NON_JSON_RESPONSE', '서버 응답을 이해할 수 없습니다.');
  }

  if (!res.ok) {
    const err = (body as ErrorBody).error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `요청에 실패했습니다 (HTTP ${res.status}).`,
      err?.details,
    );
  }

  return body as T;
}

/** 쿼리스트링을 만든다. undefined/빈문자열 값은 뺀다. */
export function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * 파일(CSV 등)을 내려받는 POST 요청.
 *
 * reports 의 CSV 는 GET 이라 <a href> 로 그냥 링크를 걸면 됐다. lookup 은 코드를
 * 최대 20만 자까지 붙여넣을 수 있어 POST 로만 보낼 수 있다 - 그래서 fetch 로
 * 직접 받아 Blob 으로 돌려준다. 호출부가 그 Blob 으로 다운로드를 띄운다.
 */
async function postBlob(path: string, body: unknown): Promise<Blob> {
  const token = csrfToken();
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text();
    const parsed: unknown = text ? JSON.parse(text) : {};
    const err = (parsed as ErrorBody).error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `요청에 실패했습니다 (HTTP ${res.status}).`,
      err?.details,
    );
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  postBlob,
};
